import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../ui';
import { DesignerState } from './DesignerFlow';
import { Loader2Icon, SparklesIcon, CalendarIcon, UsersIcon, MapPinIcon, TargetIcon, CheckCircle2Icon, BookOpenIcon, HeartPulseIcon, MessageSquareIcon, ActivityIcon, ArrowRightIcon } from 'lucide-react';
interface Props {
  data: DesignerState;
}
export function Step6Blueprint({
  data
}: Props) {
  const [isGenerating, setIsGenerating] = useState(true);
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsGenerating(false);
    }, 2000);
    return () => clearTimeout(timer);
  }, []);
  if (isGenerating) {
    return <div className="flex flex-col items-center justify-center min-h-[60vh]" data-id="element-2593">
        <Loader2Icon className="w-12 h-12 text-brand-teal animate-spin mb-6" data-id="element-2594" />
        <h2 className="text-2xl font-bold text-brand-navy mb-2" data-id="element-2595">
          Crafting your blueprint...
        </h2>
        <p className="text-slate-500" data-id="element-2596">
          Analyzing goals and preferences for {data.eventName || 'your event'}
        </p>
      </div>;
  }
  const strategyText = `Based on your goal of ${data.goals[0]?.toLowerCase() || 'alignment'} for ${data.attendees || 'your'} attendees, we recommend a ${data.format?.toLowerCase() || 'balanced'} approach. The ${data.energyLevel?.toLowerCase() || 'active'} energy level pairs perfectly with your selected learning themes, ensuring the ${data.audienceType?.toLowerCase() || 'group'} remains engaged without burning out.`;
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} className="max-w-4xl mx-auto" data-id="element-2597">
      {/* Header & Summary */}
      <div className="bg-brand-navy text-white rounded-t-2xl p-8 md:p-10 relative overflow-hidden" data-id="element-2598">
        <div className="absolute top-0 right-0 w-64 h-64 bg-brand-teal/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-2599" />
        <div className="relative z-10" data-id="element-2600">
          <div className="flex items-center gap-3 mb-6" data-id="element-2601">
            <div className="bg-white/10 p-2 rounded-lg" data-id="element-2602">
              <SparklesIcon className="w-6 h-6 text-brand-coral" data-id="element-2603" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold" data-id="element-2604">
              Your Offsite Blueprint
            </h1>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 bg-white/5 border border-white/10 rounded-xl p-6" data-id="element-2605">
            <div data-id="element-2606">
              <div className="text-slate-400 text-xs uppercase tracking-wider mb-1" data-id="element-2607">
                Event
              </div>
              <div className="font-semibold" data-id="element-2608">
                {data.eventName || 'Untitled Event'}
              </div>
            </div>
            <div data-id="element-2609">
              <div className="text-slate-400 text-xs uppercase tracking-wider mb-1" data-id="element-2610">
                Location
              </div>
              <div className="font-semibold flex items-center gap-1" data-id="element-2611">
                <MapPinIcon className="w-4 h-4" data-id="element-2612" /> {data.location || 'TBD'}
              </div>
            </div>
            <div data-id="element-2613">
              <div className="text-slate-400 text-xs uppercase tracking-wider mb-1" data-id="element-2614">
                Attendees
              </div>
              <div className="font-semibold flex items-center gap-1" data-id="element-2615">
                <UsersIcon className="w-4 h-4" data-id="element-2616" /> {data.attendees || '0'}
              </div>
            </div>
            <div data-id="element-2617">
              <div className="text-slate-400 text-xs uppercase tracking-wider mb-1" data-id="element-2618">
                Length
              </div>
              <div className="font-semibold flex items-center gap-1" data-id="element-2619">
                <CalendarIcon className="w-4 h-4" data-id="element-2620" /> {data.length || 'TBD'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Blueprint Content */}
      <div className="bg-white rounded-b-2xl shadow-sm border border-slate-200 border-t-0 p-8 md:p-10 space-y-12" data-id="element-2621">
        {/* 1. Strategy */}
        <section data-id="element-2622">
          <h3 className="text-xl font-bold text-brand-navy mb-4 flex items-center gap-2" data-id="element-2623">
            <TargetIcon className="w-5 h-5 text-brand-teal" data-id="element-2624" /> Recommended Event
            Strategy
          </h3>
          <p className="text-slate-600 leading-relaxed text-lg" data-id="element-2625">
            {strategyText}
          </p>
        </section>

        {/* 2. Agenda */}
        <section data-id="element-2626">
          <h3 className="text-xl font-bold text-brand-navy mb-6 flex items-center gap-2" data-id="element-2627">
            <CalendarIcon className="w-5 h-5 text-brand-teal" data-id="element-2628" /> Suggested
            Agenda
          </h3>
          <div className="space-y-4" data-id="element-2629">
            {[{
            day: 'Before Arrival',
            items: ['attendee intake', 'introductions', 'goals and preferences', 'pre-event reflection']
          }, {
            day: 'Day 1',
            items: ['arrival and welcome', 'group orientation', 'connection exercise', 'dinner prompt']
          }, {
            day: 'Day 2',
            items: ['morning wellness option', 'leadership session', 'small group exercise', 'activity block', 'reflection and commitments']
          }, {
            day: 'After Event',
            items: ['post-event survey', 'commitment tracking', '30-day follow-up']
          }].map((block, i) => <div key={i} className="flex gap-4" data-id="element-2630">
                <div className="w-32 shrink-0 pt-4 text-sm font-bold text-slate-400 uppercase tracking-wider text-right" data-id="element-2631">
                  {block.day}
                </div>
                <div className="flex-1 bg-slate-50 border border-slate-100 rounded-xl p-5" data-id="element-2632">
                  <ul className="grid sm:grid-cols-2 gap-3" data-id="element-2633">
                    {block.items.map((item, j) => <li key={j} className="flex items-center gap-2 text-sm font-medium text-slate-700" data-id="element-2634">
                        <div className="w-1.5 h-1.5 rounded-full bg-brand-coral" data-id="element-2635" />
                        {item}
                      </li>)}
                  </ul>
                </div>
              </div>)}
          </div>
        </section>

        {/* 3. Learning Pathway */}
        <section data-id="element-2636">
          <h3 className="text-xl font-bold text-brand-navy mb-4 flex items-center gap-2" data-id="element-2637">
            <BookOpenIcon className="w-5 h-5 text-brand-teal" data-id="element-2638" /> Learning
            Pathway
          </h3>
          <div className="grid sm:grid-cols-2 gap-4" data-id="element-2639">
            {['Leadership Reset', 'Team Trust', 'Wellness and Energy', 'Communication Under Pressure'].map((mod, i) => <div key={i} className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3 shadow-sm" data-id="element-2640">
                <div className="bg-brand-navy/5 p-2 rounded-lg text-brand-navy" data-id="element-2641">
                  <CheckCircle2Icon className="w-5 h-5" data-id="element-2642" />
                </div>
                <span className="font-semibold text-slate-800" data-id="element-2643">{mod}</span>
              </div>)}
          </div>
        </section>

        {/* 4. Group Experience & 5. Wellness */}
        <div className="grid md:grid-cols-2 gap-8" data-id="element-2644">
          <section data-id="element-2645">
            <h3 className="text-xl font-bold text-brand-navy mb-4 flex items-center gap-2" data-id="element-2646">
              <UsersIcon className="w-5 h-5 text-brand-teal" data-id="element-2647" /> Group Experience
              Design
            </h3>
            <div className="space-y-3" data-id="element-2648">
              {['buddy pairs', 'cross-functional pods', 'reflection triads', 'activity groups'].map((item, i) => <div key={i} className="bg-slate-50 border border-slate-100 rounded-lg p-3 text-sm font-medium text-slate-700" data-id="element-2649">
                  {item}
                </div>)}
            </div>
          </section>
          <section data-id="element-2650">
            <h3 className="text-xl font-bold text-brand-navy mb-4 flex items-center gap-2" data-id="element-2651">
              <HeartPulseIcon className="w-5 h-5 text-brand-teal" data-id="element-2652" /> Wellness
              Recommendations
            </h3>
            <div className="space-y-3" data-id="element-2653">
              {['morning movement', 'lighter food option', 'optional alcohol approach', 'recovery window', 'quiet reflection time'].map((item, i) => <div key={i} className="bg-slate-50 border border-slate-100 rounded-lg p-3 text-sm font-medium text-slate-700" data-id="element-2654">
                  {item}
                </div>)}
            </div>
          </section>
        </div>

        {/* 6. Intake Questions */}
        <section data-id="element-2655">
          <h3 className="text-xl font-bold text-brand-navy mb-4 flex items-center gap-2" data-id="element-2656">
            <MessageSquareIcon className="w-5 h-5 text-brand-teal" data-id="element-2657" /> Suggested
            Intake Questions
          </h3>
          <div className="bg-brand-navy text-white rounded-xl p-6 space-y-4" data-id="element-2658">
            {['What do you want to get out of this offsite?', 'What gives you energy at work?', 'What drains your energy?', 'What kind of activities do you prefer?', 'What would make this event successful for you?'].map((q, i) => <div key={i} className="flex gap-4" data-id="element-2659">
                <span className="text-brand-teal font-mono font-bold" data-id="element-2660">
                  {i + 1}.
                </span>
                <span className="text-slate-200" data-id="element-2661">{q}</span>
              </div>)}
          </div>
        </section>

        {/* 7. Success Metrics */}
        <section data-id="element-2662">
          <h3 className="text-xl font-bold text-brand-navy mb-4 flex items-center gap-2" data-id="element-2663">
            <ActivityIcon className="w-5 h-5 text-brand-teal" data-id="element-2664" /> Success Metrics
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4" data-id="element-2665">
            {['intake completion', 'engagement rate', 'connection score', 'clarity score', 'attendee satisfaction', 'commitments captured'].map((metric, i) => <div key={i} className="bg-white border border-slate-200 rounded-xl p-4 text-center shadow-sm" data-id="element-2666">
                <div className="text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-2667">
                  {metric}
                </div>
              </div>)}
          </div>
        </section>
      </div>

      {/* Step 7: Upgrade CTA */}
      <div className="mt-12 bg-white rounded-2xl shadow-lg border-2 border-brand-teal/20 p-8 md:p-12 text-center relative overflow-hidden" data-id="element-2668">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-brand-teal via-brand-coral to-brand-teal" data-id="element-2669" />

        <h2 className="text-3xl font-bold text-brand-navy mb-4" data-id="element-2670">
          Want to turn this into a branded event hub?
        </h2>
        <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto" data-id="element-2671">
          Offsite+ can convert this blueprint into a live event hub with
          attendee intake, agenda, learning prompts, group matching, wellness
          preferences, and a client-ready post-event report.
        </p>

        <div className="flex flex-col sm:flex-row justify-center items-center gap-4 mb-6" data-id="element-2672">
          <Button variant="accent" size="lg" href="/event-hub" className="w-full sm:w-auto" data-id="element-2673">
            Create Event Hub <ArrowRightIcon className="w-4 h-4 ml-2" data-id="element-2674" />
          </Button>
          <Button variant="outline" size="lg" href="/dashboard" className="w-full sm:w-auto" data-id="element-2675">
            Book a Demo
          </Button>
          <Button variant="ghost" size="lg" onClick={() => window.print()} className="w-full sm:w-auto" data-id="element-2676">
            Download Blueprint
          </Button>
        </div>

        <p className="text-sm font-medium text-slate-500" data-id="element-2677">
          Starter from $499/event. Professional from $1,500/event.
        </p>
      </div>
    </motion.div>;
}